import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { BookOpenCheck, Upload, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const fmtD = (iso) => iso ? iso.split('-').reverse().join('-') : '—';

const RegisterResult = ({ data }) => (
  <div className="rounded-lg px-3 py-2 text-xs border border-dash-green/30 bg-[rgba(107,203,119,.08)] text-dash-ink space-y-1.5" data-testid="fp-register-success">
    <div className="flex items-start gap-1.5 font-semibold">
      <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      Register for {fmtD(data.date)} loaded: {data.items} items ({data.receipts} received, {data.issues} consumed).
      {data.recomputed_to ? ` Dashboard rebuilt ${fmtD(data.recomputed_from)} → ${fmtD(data.recomputed_to)}.` : ''}
    </div>
    {data.new_items.length > 0 && (
      <div data-testid="fp-register-new-items">New items created: {data.new_items.map((n) => `${n.code} ${n.name} (${n.department})`).join(', ')}</div>
    )}
    {data.differences.length > 0 && (
      <div data-testid="fp-register-differences">
        <div className="text-dash-coral font-semibold">{data.differences.length} item(s) opened differently from the system's {fmtD(data.compared_with)} closing — register figures used:</div>
        <div className="max-h-48 overflow-auto mt-1">
          <table className="w-full text-[11px]">
            <thead><tr className="text-dash-mut text-left"><th className="pr-2">Code</th><th className="pr-2">Item</th><th className="pr-2 text-right">System</th><th className="text-right">Register</th></tr></thead>
            <tbody>
              {data.differences.map((x) => (
                <tr key={x.code}><td className="pr-2 font-mono">{x.code}</td><td className="pr-2">{x.name}</td>
                  <td className="pr-2 text-right font-mono">{x.system_closing}</td><td className="text-right font-mono">{x.register_opening}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

export const FeedPlantRegisterUpload = ({ onUploaded }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dates, setDates] = useState([]);

  const loadDates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/feed-plant/registers`, { withCredentials: true });
      setDates(r.data.dates || []);
    } catch (e) {}
  }, []);
  useEffect(() => { loadDates(); }, [loadDates]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/api/feed-plant/upload-register`, fd, { withCredentials: true });
      setResult({ ok: true, data: r.data });
      loadDates();
      onUploaded && onUploaded();
    } catch (e) {
      setResult({ ok: false, message: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="bg-dash-panel border border-dash-line rounded-xl p-5 space-y-3" data-testid="fp-register-upload">
      <div className="flex items-center gap-2">
        <BookOpenCheck className="w-4 h-4 text-dash-ice" />
        <h3 className="text-sm font-semibold text-dash-ink">Daily Stock Register upload</h3>
      </div>
      <p className="text-xs text-dash-mut">Upload the day's "Closing Stock" register exactly as it is (.xlsx). That day is rebuilt from the register — opening, received and consumption — and every later day is recomputed. Re-uploading a date replaces it; manual entries on a register day are ignored.</p>
      <button onClick={() => fileRef.current?.click()} disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-dash-ice text-white disabled:opacity-40 transition-opacity"
        data-testid="fp-register-upload-btn">
        <Upload className="w-3.5 h-3.5" /> {busy ? 'Processing register…' : 'Upload Stock Register'}
      </button>
      <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="fp-register-upload-input" />
      {result?.ok && <RegisterResult data={result.data} />}
      {result && !result.ok && (
        <div className="rounded-lg px-3 py-2 text-xs border border-dash-coral/30 bg-[rgba(255,107,138,.08)] text-dash-coral flex items-start gap-1.5" data-testid="fp-register-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {result.message}
        </div>
      )}
      <div className="text-[11px] text-dash-mut" data-testid="fp-register-dates">
        Registers loaded: {dates.length ? dates.map(fmtD).join(', ') : 'none yet'}
      </div>
    </div>
  );
};
